import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export function useChatActions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const archiveChat = useMutation({
    mutationFn: async (chatId: string) => {
      const { error: archiveError } = await supabase
        .from('archived_chats')
        .insert({
          chat_id: chatId,
          user_id: user?.id,
          archived_at: new Date().toISOString()
        });

      if (archiveError) throw archiveError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['archived-chats', user?.id] });
    },
  });

  const restoreChat = useMutation({
    mutationFn: async (chatId: string) => {
      const { error: restoreError } = await supabase
        .from('archived_chats')
        .delete()
        .eq('chat_id', chatId)
        .eq('user_id', user?.id);

      if (restoreError) throw restoreError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['archived-chats', user?.id] });
    },
  });

  const deleteChat = useMutation({
    mutationFn: async (chatId: string) => {
      // First remove it from archived_chats if it's there
      const { error: archiveError } = await supabase
        .from('archived_chats')
        .delete()
        .eq('chat_id', chatId)
        .eq('user_id', user?.id);
        
      if (archiveError) {
        console.error('Error deleting from archived_chats:', archiveError);
        // Continue anyway to try removing the participant
      }

      // Then remove the user as a participant
      const { error } = await supabase
        .from('chat_participants')
        .delete()
        .eq('chat_id', chatId)
        .eq('user_id', user?.id);

      if (error) throw error;
      
      // After removing the participant, check if there are any left
      const { data: remainingParticipants, error: countError } = await supabase
        .from('chat_participants')
        .select('user_id')
        .eq('chat_id', chatId);
        
      if (countError) {
        console.error('Error checking remaining participants:', countError);
        return; // Still return success since we've deleted the user's participation
      }
      
      // If no participants left, delete the chat entirely
      if (!remainingParticipants || remainingParticipants.length === 0) {
        console.log('No participants left, deleting chat:', chatId);
        
        // Delete all messages first
        const { error: messagesError } = await supabase
          .from('messages')
          .delete()
          .eq('chat_id', chatId);
          
        if (messagesError) {
          console.error('Error deleting messages:', messagesError);
          // Continue anyway to try deleting the chat
        }
        
        // Then delete the chat itself
        const { error: chatError } = await supabase
          .from('chats')
          .delete()
          .eq('id', chatId);
          
        if (chatError) {
          console.error('Error deleting chat:', chatError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['archived-chats', user?.id] });
    },
  });

  const markChatAsRead = useMutation({
    mutationFn: async (chatId: string) => {
      const { error } = await supabase
        .from('chat_participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('chat_id', chatId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats', user?.id] });
    },
  });

  return {
    archiveChat,
    restoreChat,
    deleteChat,
    markChatAsRead,
  };
} 